// src/components/Admin/QuestionReceived.js
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import QuestionsContainer from './QuestionsContainer';
import PopupWrapper from '../../components/UI/PopupWrapper';
import AnswerQuestionModal from '../../components/Modals/AnswerQuestionModal';
import ViewAnswerModal from '../../components/Modals/ViewAnswerModal';
import ReceivedPopup from '../../components/UI/ReceivedPopup';

const QuestionReceived = () => {
  const { currentUser } = useContext(AuthContext);
  const [questions, setQuestions] = useState([]);
  const [activePopupId, setActivePopupId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Load only non-curated questions for the current user on mount.
  useEffect(() => {
    fetchQuestions();
  }, [currentUser]);

  const fetchQuestions = () => {
    fetch('/api/questions')
      .then(res => res.json())
      .then(data => {
        // Filter out curated questions and those hidden by the receiver.
        let filtered = (data || [])
          .filter(q => !q.isCurated && !q.deletedByReceiver)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        if (currentUser && currentUser.username) {
          filtered = filtered.filter(q => q.toUsername === currentUser.username);
        }
        setQuestions(filtered);
      })
      .catch(err => console.error(err));
  };

  const handleDeleteQuestion = async (id) => {
    // Optimistically remove the question from this view.
    setQuestions(prev => prev.filter(q => q._id !== id));
    try {
      // PATCH instead of DELETE: mark as hidden for the receiver.
      const response = await fetch(`/api/questions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deletedByReceiver: true })
      });
      if (!response.ok) {
        console.error("Failed to hide question:", id);
        fetchQuestions();
      }
    } catch (err) {
      console.error(err);
      fetchQuestions();
    }
    setConfirmDeleteId(null);
  };

  const handleHideQuestion = async (id) => {
    setQuestions(prev =>
      prev.map(q => q._id === id ? { ...q, displayOnProfile: false } : q)
    );
    try {
      const response = await fetch(`/api/questions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayOnProfile: false }),
      });
      if (!response.ok) {
        console.error("Failed to hide question:", id);
        fetchQuestions();
      }
    } catch (err) {
      console.error(err);
      fetchQuestions();
    }
  };

  const handleShowQuestion = async (id) => {
    setQuestions(prev =>
      prev.map(q => q._id === id ? { ...q, displayOnProfile: true } : q)
    );
    try {
      const response = await fetch(`/api/questions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayOnProfile: true }),
      });
      if (!response.ok) {
        console.error("Failed to show question:", id);
        fetchQuestions();
      }
    } catch (err) {
      console.error(err);
      fetchQuestions();
    }
  };

  const handleReplyClick = (questionText, detail, id) => {
    setSelectedQuestion({ _id: id.toString(), question: questionText, detail });
  };

  const handleViewAnswer = (questionText, detail, id, answer, tags) => {
    setSelectedQuestion({ _id: id.toString(), question: questionText, detail, answer, tags });
  };

  const handlePopupToggle = (id) => {
    setActivePopupId(prev => (prev === id ? null : id));
    setConfirmDeleteId(null);
  };

  const handleOpenConfirm = (id) => {
    setConfirmDeleteId(id);
  };

  const handlePopupClose = () => {
    setActivePopupId(null);
    setConfirmDeleteId(null);
  };

  const handleModalClose = () => {
    setSelectedQuestion(null);
  };

  const handleAnswerPosted = (updatedQuestion) => {
    setQuestions(prev =>
      prev.map(q =>
        q._id.toString() === updatedQuestion._id.toString() ? updatedQuestion : q
      )
    );
    setSelectedQuestion(null);
  };

  const handleToggleDisplay = (q) => {
    if (q.displayOnProfile) {
      handleHideQuestion(q._id);
    } else {
      handleShowQuestion(q._id);
    }
  };

  // Filter questions based on search term.
  const filteredQuestions = questions.filter(q => {
    const search = searchTerm.toLowerCase();
    const questionText = q.question.toLowerCase();
    const dateString = new Date(q.createdAt).toLocaleDateString().toLowerCase();
    // For received questions, include sender info from "askedName" if available.
    let sender = '';
    if(q.fromUsername) sender += q.fromUsername.toLowerCase();
    if(q.askedName) sender += q.askedName.toLowerCase();
    if(q.toUsername) sender += q.toUsername.toLowerCase();
    return questionText.includes(search) || dateString.includes(search) || sender.includes(search);
  });

  const tableHead = (
    <tr>
      <th>Question</th>
      <th>Asked By</th>
      <th>Date</th>
      <th>Tip</th>
      <th>Tag</th>
      <th>Display on Profile</th>
      <th>Actions</th>
    </tr>
  );

  const renderDesktopRow = (q, index) => {
    const qId = q._id ? q._id.toString() : "";
    const displayText = q.displayOnProfile ? "Yes" : "No";
    return (
      <tr key={qId} className={index === 0 ? "highlight" : ""}>
        <td>{q.question}</td>
        <td>{q.askedName || "N/A"}</td>
        <td>{new Date(q.createdAt).toLocaleDateString()}</td>
        <td>{q.tip ? `$${q.tip}` : "$0"}</td>
        <td>
          <span className={`tag ${q.status === "Pending" ? "inactive" : ""}`}>
            {q.status}
          </span>
        </td>
        <td>{displayText}</td>
        <td className="table-action">
          {q.status === "Pending" ? (
            <button className="table-btn action-button" onClick={() => handleReplyClick(q.question, q.detail, q._id)}>
              <img src="/images/i_table_reply.svg" alt="Reply" />
            </button>
          ) : (
            <button className="table-btn action-button" onClick={() => handleViewAnswer(q.question, q.detail, q._id, q.answer, q.tags)}>
              <img src="/images/i_table_view.svg" alt="View answer" />
            </button>
          )}
          <PopupWrapper onClose={handlePopupClose}>
            <>
              <button className="table-btn" onClick={(e) => { e.stopPropagation(); handlePopupToggle(q._id); }}>
                <img className="more" src="/images/i_More.svg" alt="More" />
              </button>
              {activePopupId === q._id && (
                <ReceivedPopup
                  q={q}
                  onReply={q.status === "Pending" ? () => handleReplyClick(q.question, q.detail, q._id) : () => handleViewAnswer(q.question, q.detail, q._id, q.answer, q.tags)}
                  onDelete={() => handleDeleteQuestion(q._id)}
                  onToggleDisplay={() => handleToggleDisplay(q)}
                  onClose={handlePopupClose}
                  confirm={confirmDeleteId === q._id}
                  onOpenConfirm={() => handleOpenConfirm(q._id)}
                />
              )}
            </>
          </PopupWrapper>
        </td>
      </tr>
    );
  };

  const renderMobileCard = (q) => {
    const qId = q._id ? q._id.toString() : "";
    const mainAction = q.status === "Pending"
      ? () => handleReplyClick(q.question, q.detail, q._id)
      : () => handleViewAnswer(q.question, q.detail, q._id, q.answer, q.tags);
    const mainActionLabel = q.status === "Pending" ? "Reply" : "View answer";

    return (
      <div className="card" key={qId} style={{ position: "relative" }}>
        <p className="xlarge">{q.question}</p>
        <span className="tag">{q.tip ? `$${q.tip}` : "$0"}</span>
        <button className="grey full-width" onClick={mainAction}>
          <img src={q.status === "Pending" ? "/images/i_Reply.svg" : "/images/i_View_Answer.svg"} alt={mainActionLabel} />
        </button>
        <PopupWrapper onClose={handlePopupClose}>
          <>
            <button className="clear full-width" onClick={(e) => { e.stopPropagation(); handlePopupToggle(q._id); }}>
              <img src="/images/i_More.svg" alt="More" />
            </button>
            {activePopupId === q._id && (
              <ReceivedPopup
                q={q}
                onReply={q.status === "Pending" ? () => handleReplyClick(q.question, q.detail, q._id) : () => handleViewAnswer(q.question, q.detail, q._id, q.answer, q.tags)}
                onDelete={() => handleDeleteQuestion(q._id)}
                onToggleDisplay={() => handleToggleDisplay(q)}
                onClose={handlePopupClose}
                confirm={confirmDeleteId === q._id}
                onOpenConfirm={() => handleOpenConfirm(q._id)}
              />
            )}
          </>
        </PopupWrapper>
      </div>
    );
  };

  return (
    <>
      <div className="admin-action-header">
        <div className="search-container">
          <input 
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <QuestionsContainer
        questions={filteredQuestions}
        renderDesktopRow={renderDesktopRow}
        renderMobileCard={renderMobileCard}
        tableHead={tableHead}
      />
      {selectedQuestion && (
        selectedQuestion.answer ? (
          <ViewAnswerModal
            onClose={() => setSelectedQuestion(null)}
            question={selectedQuestion.question}
            detail={selectedQuestion.detail}
            answer={selectedQuestion.answer}
            questionId={selectedQuestion._id}
            initialTags={selectedQuestion.tags || []}
            onAnswerPosted={handleAnswerPosted}
          />
        ) : (
          <AnswerQuestionModal
            onClose={() => setSelectedQuestion(null)}
            questionId={selectedQuestion._id}
            question={selectedQuestion.question}
            detail={selectedQuestion.detail}
            onAnswerPosted={handleAnswerPosted}
          />
        )
      )}
    </>
  );
};

export default QuestionReceived;
